// SolarInstallationAbout.js - Solar installation themed About section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a solar installation themed About section with company profile and services
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the about is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The about elements
 */
export const createSolarInstallationAboutElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const aboutSectionClass = 'solar-about-section';
  const aboutContainerClass = 'solar-about-container';
  const aboutHeaderClass = 'solar-about-header';
  const aboutTitleClass = 'solar-about-title';
  const aboutSubtitleClass = 'solar-about-subtitle';
  const aboutContentClass = 'solar-about-content';
  const aboutLeftClass = 'solar-about-left';
  const aboutRightClass = 'solar-about-right';
  const aboutImageWrapperClass = 'solar-about-image-wrapper';
  const aboutImageClass = 'solar-about-image';
  const aboutStoryClass = 'solar-about-story';
  const aboutStoryTitleClass = 'solar-about-story-title';
  const aboutStoryTextClass = 'solar-about-story-text';
  const aboutServicesClass = 'solar-about-services';
  const aboutServicesTitleClass = 'solar-about-services-title';
  const aboutServicesListClass = 'solar-about-services-list';
  const aboutServiceItemClass = 'solar-about-service-item';
  const aboutServiceIconClass = 'solar-about-service-icon';
  const aboutServiceNameClass = 'solar-about-service-name';
  const aboutServiceDescClass = 'solar-about-service-desc';
  const aboutQuoteClass = 'solar-about-quote';
  const aboutQuoteTextClass = 'solar-about-quote-text';
  const aboutQuoteAuthorClass = 'solar-about-quote-author';

  // Desktop Styles - Solar installation theme with whitish background
  const aboutSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 80% 20%, rgba(74, 222, 128, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 20% 80%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)
    `,
    color: '#1e293b',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden'
  };

  const aboutContainerStyles = {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '80px',
    position: 'relative',
    zIndex: '1'
  };

  const aboutHeaderStyles = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const aboutTitleStyles = {
    fontSize: '56px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '24px',
    letterSpacing: '-0.02em',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutSubtitleStyles = {
    fontSize: '20px',
    fontWeight: '400',
    color: '#64748b',
    lineHeight: '1.6',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutContentStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'start'
  };

  const aboutLeftStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    background: `
      linear-gradient(135deg, rgba(74, 222, 128, 0.08) 0%, rgba(34, 197, 94, 0.04) 100%),
      rgba(255, 255, 255, 0.9)
    `,
    padding: '40px',
    borderRadius: '12px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  };

  const aboutRightStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  };

  const aboutImageWrapperStyles = {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: `
      0 20px 40px -8px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(74, 222, 128, 0.3),
      0 0 30px rgba(74, 222, 128, 0.1)
    `,
    border: '2px solid #4ade80',
    alignSelf: 'center',
    maxWidth: '300px',
    width: '100%'
  };

  const aboutImageStyles = {
    width: '100%',
    height: '350px',
    objectFit: 'cover',
    transition: 'all 0.6s ease',
    filter: 'contrast(1.1) brightness(1.05)'
  };

  const aboutStoryStyles = {
    background: `
      linear-gradient(135deg, rgba(74, 222, 128, 0.08) 0%, rgba(34, 197, 94, 0.04) 100%),
      rgba(255, 255, 255, 0.9)
    `,
    padding: '40px',
    borderRadius: '12px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  };

  const aboutStoryTitleStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#059669',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutStoryTextStyles = {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#475569',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutServicesStyles = {
    background: 'transparent',
    padding: '0',
    borderRadius: '0',
    border: 'none',
    backdropFilter: 'none'
  };

  const aboutServicesTitleStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#059669',
    marginBottom: '32px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutServicesListStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const aboutServiceItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    background: `
      linear-gradient(135deg, rgba(74, 222, 128, 0.05) 0%, rgba(34, 197, 94, 0.02) 100%),
      rgba(255, 255, 255, 0.8)
    `,
    borderRadius: '8px',
    border: '1px solid rgba(74, 222, 128, 0.15)',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
  };

  const aboutServiceIconStyles = {
    width: '48px',
    height: '48px',
    backgroundColor: '#4ade80',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#0f2341',
    flexShrink: '0'
  };

  const aboutServiceNameStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutServiceDescStyles = {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#64748b',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutQuoteStyles = {
    textAlign: 'center',
    padding: '60px 40px',
    background: `
      linear-gradient(135deg, rgba(74, 222, 128, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%),
      rgba(255, 255, 255, 0.95)
    `,
    borderRadius: '12px',
    border: '1px solid rgba(74, 222, 128, 0.25)',
    backdropFilter: 'blur(20px)',
    marginTop: '40px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
  };

  const aboutQuoteTextStyles = {
    fontSize: '24px',
    fontWeight: '500',
    color: '#1e293b',
    lineHeight: '1.6',
    fontStyle: 'italic',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutQuoteAuthorStyles = {
    fontSize: '16px',
    color: '#059669',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif'
  };

  // Hover styles
  const aboutServiceItemHoverStyles = {
    background: `
      linear-gradient(135deg, rgba(74, 222, 128, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%),
      rgba(255, 255, 255, 0.95)
    `,
    border: '1px solid rgba(74, 222, 128, 0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(74, 222, 128, 0.15)'
  };

  // Tablet Styles
  const tabletAboutContentStyles = {
    ...aboutContentStyles,
    gridTemplateColumns: '1fr',
    gap: '60px'
  };

  const tabletAboutTitleStyles = {
    ...aboutTitleStyles,
    fontSize: '48px'
  };

  const tabletAboutLeftStyles = {
    ...aboutLeftStyles,
    padding: '32px'
  };

  const tabletAboutStoryStyles = {
    ...aboutStoryStyles,
    padding: '32px'
  };

  // Mobile Styles
  const mobileAboutSectionStyles = {
    ...aboutSectionStyles,
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const mobileAboutContainerStyles = {
    ...aboutContainerStyles,
    gap: '60px',
    padding: '0 20px'
  };

  const mobileAboutTitleStyles = {
    ...aboutTitleStyles,
    fontSize: '36px'
  };

  const mobileAboutSubtitleStyles = {
    ...aboutSubtitleStyles,
    fontSize: '18px'
  };

  const mobileAboutLeftStyles = {
    ...aboutLeftStyles,
    padding: '24px'
  };

  const mobileAboutStoryStyles = {
    ...aboutStoryStyles,
    padding: '24px'
  };

  const mobileAboutQuoteStyles = {
    ...aboutQuoteStyles,
    padding: '40px 24px'
  };

  const mobileAboutQuoteTextStyles = {
    ...aboutQuoteTextStyles,
    fontSize: '20px'
  };

  const mobileAboutServiceItemStyles = {
    ...aboutServiceItemStyles,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '20px 16px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(aboutSectionClass, aboutSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContainerClass, aboutContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutHeaderClass, aboutHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutTitleClass, aboutTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSubtitleClass, aboutSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContentClass, aboutContentStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutLeftClass, aboutLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutRightClass, aboutRightStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageWrapperClass, aboutImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageClass, aboutImageStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryClass, aboutStoryStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryTitleClass, aboutStoryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryTextClass, aboutStoryTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServicesClass, aboutServicesStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServicesTitleClass, aboutServicesTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServicesListClass, aboutServicesListStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServiceItemClass, aboutServiceItemStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServiceIconClass, aboutServiceIconStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServiceNameClass, aboutServiceNameStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutServiceDescClass, aboutServiceDescStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteClass, aboutQuoteStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteTextClass, aboutQuoteTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteAuthorClass, aboutQuoteAuthorStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${aboutServiceItemClass}--hover`, aboutServiceItemHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(aboutContentClass, tabletAboutContentStyles);
    newRegistry[DEVICES.TABLET].set(aboutTitleClass, tabletAboutTitleStyles);
    newRegistry[DEVICES.TABLET].set(aboutLeftClass, tabletAboutLeftStyles);
    newRegistry[DEVICES.TABLET].set(aboutStoryClass, tabletAboutStoryStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(aboutSectionClass, mobileAboutSectionStyles);
    newRegistry[DEVICES.MOBILE].set(aboutContainerClass, mobileAboutContainerStyles);
    newRegistry[DEVICES.MOBILE].set(aboutTitleClass, mobileAboutTitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutSubtitleClass, mobileAboutSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutLeftClass, mobileAboutLeftStyles);
    newRegistry[DEVICES.MOBILE].set(aboutStoryClass, mobileAboutStoryStyles);
    newRegistry[DEVICES.MOBILE].set(aboutQuoteClass, mobileAboutQuoteStyles);
    newRegistry[DEVICES.MOBILE].set(aboutQuoteTextClass, mobileAboutQuoteTextStyles);
    newRegistry[DEVICES.MOBILE].set(aboutServiceItemClass, mobileAboutServiceItemStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    aboutSectionClass,
    aboutContainerClass,
    aboutHeaderClass,
    aboutTitleClass,
    aboutSubtitleClass,
    aboutContentClass,
    aboutLeftClass,
    aboutRightClass,
    aboutImageWrapperClass,
    aboutImageClass,
    aboutStoryClass,
    aboutStoryTitleClass,
    aboutStoryTextClass,
    aboutServicesClass,
    aboutServicesTitleClass,
    aboutServicesListClass,
    aboutServiceItemClass,
    aboutServiceIconClass,
    aboutServiceNameClass,
    aboutServiceDescClass,
    aboutQuoteClass,
    aboutQuoteTextClass,
    aboutQuoteAuthorClass
  ]));

  // Generate IDs for all elements
  const aboutSectionId = Date.now();
  const containerId = aboutSectionId + 1;
  const headerId = aboutSectionId + 2;
  const titleId = aboutSectionId + 3;
  const subtitleId = aboutSectionId + 4;
  const contentId = aboutSectionId + 5;
  const leftId = aboutSectionId + 6;
  const rightId = aboutSectionId + 7;
  const imageWrapperId = aboutSectionId + 8;
  const imageId = aboutSectionId + 9;
  const servicesId = aboutSectionId + 10;
  const servicesTitleId = aboutSectionId + 11;
  const servicesListId = aboutSectionId + 12;
  const service1Id = aboutSectionId + 13;
  const service1IconId = aboutSectionId + 14;
  const service1ContentId = aboutSectionId + 15;
  const service1NameId = aboutSectionId + 16;
  const service1DescId = aboutSectionId + 17;
  const service2Id = aboutSectionId + 18;
  const service2IconId = aboutSectionId + 19;
  const service2ContentId = aboutSectionId + 20;
  const service2NameId = aboutSectionId + 21;
  const service2DescId = aboutSectionId + 22;
  const service3Id = aboutSectionId + 23;
  const service3IconId = aboutSectionId + 24;
  const service3ContentId = aboutSectionId + 25;
  const service3NameId = aboutSectionId + 26;
  const service3DescId = aboutSectionId + 27;
  const service4Id = aboutSectionId + 28;
  const service4IconId = aboutSectionId + 29;
  const service4ContentId = aboutSectionId + 30;
  const service4NameId = aboutSectionId + 31;
  const service4DescId = aboutSectionId + 32;
  const storyId = aboutSectionId + 33;
  const storyTitleId = aboutSectionId + 34;
  const storyTextId = aboutSectionId + 35;
  const quoteId = aboutSectionId + 36;
  const quoteTextId = aboutSectionId + 37;
  const quoteAuthorId = aboutSectionId + 38;

  const elements = [
    // Main About Section
    {
      id: aboutSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Solar Installation About Section',
      parentId: null,
      className: aboutSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'solar-about-section',
        'data-component': 'section',
        'data-section-type': 'about',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Container',
      parentId: aboutSectionId,
      hierarchyName: "About Container",
      className: aboutContainerClass,
      styles: {},
      children: [headerId, contentId, quoteId],
      text: '',
      attributes: {
        'data-element': 'about-container'
      }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Header',
      parentId: containerId,
      hierarchyName: "About Header",
      className: aboutHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'about-header'
      }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'About Title',
      parentId: headerId,
      hierarchyName: "About Title",
      className: aboutTitleClass,
      styles: {},
      children: [],
      text: 'About JNT & Co.',
      attributes: {
        'data-element': 'about-title'
      }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'About Subtitle',
      parentId: headerId,
      hierarchyName: "About Subtitle",
      className: aboutSubtitleClass,
      styles: {},
      children: [],
      text: 'Leading provider of solar installation and electrical services, committed to sustainable energy solutions and cutting-edge technology.',
      attributes: {
        'data-element': 'about-subtitle'
      }
    },

    // Content
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Content',
      parentId: containerId,
      hierarchyName: "About Content",
      className: aboutContentClass,
      styles: {},
      children: [leftId, rightId],
      text: '',
      attributes: {
        'data-element': 'about-content'
      }
    },

    // Left Column
    {
      id: leftId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Left',
      parentId: contentId,
      hierarchyName: "About Left",
      className: aboutLeftClass,
      styles: {},
      children: [imageWrapperId, servicesId],
      text: '',
      attributes: {
        'data-element': 'about-left'
      }
    },

    // Right Column
    {
      id: rightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Right',
      parentId: contentId,
      hierarchyName: "About Right",
      className: aboutRightClass,
      styles: {},
      children: [storyId],
      text: '',
      attributes: {
        'data-element': 'about-right'
      }
    },

    // Image Wrapper
    {
      id: imageWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Image Wrapper',
      parentId: leftId,
      hierarchyName: "About Image Wrapper",
      className: aboutImageWrapperClass,
      styles: {},
      children: [imageId],
      text: '',
      attributes: {
        'data-element': 'about-image-wrapper'
      }
    },

    // About Image
    {
      id: imageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'About Image',
      parentId: imageWrapperId,
      hierarchyName: "About Image",
      className: aboutImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attributes: {
        'data-element': 'about-image',
        'alt': 'Solar installation team working on roof'
      }
    },

    // Story Section
    {
      id: storyId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Story',
      parentId: rightId,
      hierarchyName: "About Story",
      className: aboutStoryClass,
      styles: {},
      children: [storyTitleId, storyTextId],
      text: '',
      attributes: {
        'data-element': 'about-story'
      }
    },

    // Story Title
    {
      id: storyTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Story Title',
      parentId: storyId,
      hierarchyName: "Story Title",
      className: aboutStoryTitleClass,
      styles: {},
      children: [],
      text: 'Our Mission',
      attributes: {
        'data-element': 'about-story-title'
      }
    },

    // Story Text
    {
      id: storyTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Story Text',
      parentId: storyId,
      hierarchyName: "Story Text",
      className: aboutStoryTextClass,
      styles: {},
      children: [],
      text: 'With over 15 years of experience in the electrical and solar industry, JNT & Co. has established itself as a trusted leader in sustainable energy solutions. We specialize in comprehensive installation and repair services that help our clients transition to clean, renewable energy while maintaining the highest standards of safety and reliability.\n\nOur team of certified professionals is dedicated to providing innovative solutions that not only reduce energy costs but also contribute to a more sustainable future. From residential solar installations to complex commercial security systems, we approach every project with precision, expertise, and a commitment to excellence.\n\nWe believe that quality workmanship and customer satisfaction are the foundations of our success. Every installation is backed by our comprehensive warranty and ongoing support, ensuring that your investment in clean energy technology delivers long-term value and peace of mind.',
      attributes: {
        'data-element': 'about-story-text'
      }
    },

    // Services Section
    {
      id: servicesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Services',
      parentId: leftId,
      hierarchyName: "About Services",
      className: aboutServicesClass,
      styles: {},
      children: [servicesTitleId, servicesListId],
      text: '',
      attributes: {
        'data-element': 'about-services'
      }
    },

    // Services Title
    {
      id: servicesTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Services Title',
      parentId: servicesId,
      hierarchyName: "Services Title",
      className: aboutServicesTitleClass,
      styles: {},
      children: [],
      text: 'Our Services',
      attributes: {
        'data-element': 'about-services-title'
      }
    },

    // Services List
    {
      id: servicesListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services List',
      parentId: servicesId,
      hierarchyName: "Services List",
      className: aboutServicesListClass,
      styles: {},
      children: [service1Id, service2Id, service3Id, service4Id],
      text: '',
      attributes: {
        'data-element': 'about-services-list'
      }
    },

    // Service 1 - Solar System
    {
      id: service1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 1',
      parentId: servicesListId,
      hierarchyName: "Service 1",
      className: aboutServiceItemClass,
      styles: {},
      children: [service1IconId, service1ContentId],
      text: '',
      attributes: {
        'data-element': 'about-service-item'
      }
    },

    {
      id: service1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 1 Icon',
      parentId: service1Id,
      hierarchyName: "Service 1 Icon",
      className: aboutServiceIconClass,
      styles: {},
      children: [],
      text: '☀️',
      attributes: {
        'data-element': 'about-service-icon'
      }
    },

    {
      id: service1ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 1 Content',
      parentId: service1Id,
      hierarchyName: "Service 1 Content",
      className: 'service-content',
      styles: {},
      children: [service1NameId, service1DescId],
      text: '',
      attributes: {
        'data-element': 'service-content'
      }
    },

    {
      id: service1NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 1 Name',
      parentId: service1ContentId,
      hierarchyName: "Service 1 Name",
      className: aboutServiceNameClass,
      styles: {},
      children: [],
      text: 'Solar System Installation & Repair',
      attributes: {
        'data-element': 'about-service-name'
      }
    },

    {
      id: service1DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 1 Description',
      parentId: service1ContentId,
      hierarchyName: "Service 1 Description",
      className: aboutServiceDescClass,
      styles: {},
      children: [],
      text: 'Complete solar panel installation, maintenance, and repair services for residential and commercial properties.',
      attributes: {
        'data-element': 'about-service-desc'
      }
    },

    // Service 2 - CCTV
    {
      id: service2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 2',
      parentId: servicesListId,
      hierarchyName: "Service 2",
      className: aboutServiceItemClass,
      styles: {},
      children: [service2IconId, service2ContentId],
      text: '',
      attributes: {
        'data-element': 'about-service-item'
      }
    },

    {
      id: service2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 2 Icon',
      parentId: service2Id,
      hierarchyName: "Service 2 Icon",
      className: aboutServiceIconClass,
      styles: {},
      children: [],
      text: '📹',
      attributes: {
        'data-element': 'about-service-icon'
      }
    },

    {
      id: service2ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 2 Content',
      parentId: service2Id,
      hierarchyName: "Service 2 Content",
      className: 'service-content',
      styles: {},
      children: [service2NameId, service2DescId],
      text: '',
      attributes: {
        'data-element': 'service-content'
      }
    },

    {
      id: service2NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 2 Name',
      parentId: service2ContentId,
      hierarchyName: "Service 2 Name",
      className: aboutServiceNameClass,
      styles: {},
      children: [],
      text: 'CCTV Installation & Repair',
      attributes: {
        'data-element': 'about-service-name'
      }
    },

    {
      id: service2DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 2 Description',
      parentId: service2ContentId,
      hierarchyName: "Service 2 Description",
      className: aboutServiceDescClass,
      styles: {},
      children: [],
      text: 'Professional security camera systems installation, configuration, and maintenance for enhanced property protection.',
      attributes: {
        'data-element': 'about-service-desc'
      }
    },

    // Service 3 - Electrical Door Lock
    {
      id: service3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 3',
      parentId: servicesListId,
      hierarchyName: "Service 3",
      className: aboutServiceItemClass,
      styles: {},
      children: [service3IconId, service3ContentId],
      text: '',
      attributes: {
        'data-element': 'about-service-item'
      }
    },

    {
      id: service3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 3 Icon',
      parentId: service3Id,
      hierarchyName: "Service 3 Icon",
      className: aboutServiceIconClass,
      styles: {},
      children: [],
      text: '🔐',
      attributes: {
        'data-element': 'about-service-icon'
      }
    },

    {
      id: service3ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 3 Content',
      parentId: service3Id,
      hierarchyName: "Service 3 Content",
      className: 'service-content',
      styles: {},
      children: [service3NameId, service3DescId],
      text: '',
      attributes: {
        'data-element': 'service-content'
      }
    },

    {
      id: service3NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 3 Name',
      parentId: service3ContentId,
      hierarchyName: "Service 3 Name",
      className: aboutServiceNameClass,
      styles: {},
      children: [],
      text: 'Electrical Door Lock Systems',
      attributes: {
        'data-element': 'about-service-name'
      }
    },

    {
      id: service3DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 3 Description',
      parentId: service3ContentId,
      hierarchyName: "Service 3 Description",
      className: aboutServiceDescClass,
      styles: {},
      children: [],
      text: 'Smart lock installation and repair services including keypad, card access, and biometric door lock systems.',
      attributes: {
        'data-element': 'about-service-desc'
      }
    },

    // Service 4 - Intercoms PABX
    {
      id: service4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 4',
      parentId: servicesListId,
      hierarchyName: "Service 4",
      className: aboutServiceItemClass,
      styles: {},
      children: [service4IconId, service4ContentId],
      text: '',
      attributes: {
        'data-element': 'about-service-item'
      }
    },

    {
      id: service4IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 4 Icon',
      parentId: service4Id,
      hierarchyName: "Service 4 Icon",
      className: aboutServiceIconClass,
      styles: {},
      children: [],
      text: '📞',
      attributes: {
        'data-element': 'about-service-icon'
      }
    },

    {
      id: service4ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 4 Content',
      parentId: service4Id,
      hierarchyName: "Service 4 Content",
      className: 'service-content',
      styles: {},
      children: [service4NameId, service4DescId],
      text: '',
      attributes: {
        'data-element': 'service-content'
      }
    },

    {
      id: service4NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 4 Name',
      parentId: service4ContentId,
      hierarchyName: "Service 4 Name",
      className: aboutServiceNameClass,
      styles: {},
      children: [],
      text: 'Intercoms & PABX Systems',
      attributes: {
        'data-element': 'about-service-name'
      }
    },

    {
      id: service4DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Service 4 Description',
      parentId: service4ContentId,
      hierarchyName: "Service 4 Description",
      className: aboutServiceDescClass,
      styles: {},
      children: [],
      text: 'Professional intercom and PABX telephone system installation, configuration, and maintenance services.',
      attributes: {
        'data-element': 'about-service-desc'
      }
    },

    // Quote Section
    {
      id: quoteId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Quote',
      parentId: containerId,
      hierarchyName: "About Quote",
      className: aboutQuoteClass,
      styles: {},
      children: [quoteTextId, quoteAuthorId],
      text: '',
      attributes: {
        'data-element': 'about-quote'
      }
    },

    // Quote Text
    {
      id: quoteTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'blockquote',
      name: 'Quote Text',
      parentId: quoteId,
      hierarchyName: "Quote Text",
      className: aboutQuoteTextClass,
      styles: {},
      children: [],
      text: '"Our commitment to excellence and sustainable energy solutions drives everything we do. We don\'t just install systems – we build lasting relationships and contribute to a cleaner, more efficient future."',
      attributes: {
        'data-element': 'about-quote-text'
      }
    },

    // Quote Author
    {
      id: quoteAuthorId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cite',
      name: 'Quote Author',
      parentId: quoteId,
      hierarchyName: "Quote Author",
      className: aboutQuoteAuthorClass,
      styles: {},
      children: [],
      text: '— JNT & Co. Team',
      attributes: {
        'data-element': 'about-quote-author'
      }
    }
  ];

  return {
    aboutSection: elements[0],
    elements: elements
  };
}; 